import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GiFairyWings } from "react-icons/gi";
import { FaCalendarAlt, FaMapMarkerAlt, FaArrowLeft } from "react-icons/fa";
import "./EventDetail.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const formatTimeAmPm = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hours = parseInt(h, 10);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hr12 = hours % 12 || 12;
    return `${hr12.toString().padStart(2, '0')}:${m} ${suffix}`;
};

const formatEventDateTime = (start, end, sTime, eTime) => {
    if (!start) return '';
    
    const startDateStr = new Date(start).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const isSameDay = !end || (start.split('T')[0] === end.split('T')[0]);
    
    const startTimeStr = sTime ? formatTimeAmPm(sTime) : '';
    const endTimeStr = eTime ? formatTimeAmPm(eTime) : '';

    if (isSameDay) {
        let timeStr = '';
        if (startTimeStr && endTimeStr) {
            timeStr = ` (Start Time: ${startTimeStr} - End Time: ${endTimeStr})`;
        } else if (startTimeStr) {
            timeStr = ` (Start Time: ${startTimeStr})`;
        } else if (endTimeStr) {
            timeStr = ` (End Time: ${endTimeStr})`;
        }
        return `${startDateStr}${timeStr}`;
    } else {
        const endDateStr = new Date(end).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
        const startPart = startTimeStr ? `${startDateStr}, ${startTimeStr}` : startDateStr;
        const endPart = endTimeStr ? `${endDateStr}, ${endTimeStr}` : endDateStr;
        return `${startPart} to ${endPart}`;
    }
};

function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/events/slug/${slug}`);
        const data = await response.json();
        setEvent(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return <div className="event-detail-page-loading">Loading Event...</div>;
  }
  if (!event) return <div className="event-detail-page-loading">Event not found.</div>;

  let blocks = [];
  try {
    blocks = JSON.parse(event.contentBlocks || "[]");
  } catch (e) {
    console.log(e);
  }

  // --- EXTRACT SEO VARIABLES FOR STRUCTURED SCHEMA ---
  // 1. Image
  const firstImgBlock = blocks.find(b => b.type === "image" && b.imageUrls && b.imageUrls.length > 0);
  const eventImage = firstImgBlock ? `${API_BASE_URL}${firstImgBlock.imageUrls[0]}` : "https://rajputshikshakosh.org/default-logo.png";

  // 2. Description
  const firstTextBlock = blocks.find(b => b.type === "text" && b.content);
  const rawDescription = firstTextBlock 
    ? firstTextBlock.content.replace(/<[^>]*>?/gm, '').substring(0, 160) 
    : `Learn more about the ${event.title} event at Rajput Shiksha Kosh.`;

  // 3. Complete Schema.org Event JSON-LD (Strict validation values)
  const eventSchemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": String(event.title || ""), // Fixes "Invalid value type for field name"
    "startDate": event.eventDate,
    "endDate": event.endDate || event.eventDate,
    "eventStatus": "https://schema.org/EventScheduled", // Fixes "Missing field eventStatus"
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": event.eventPlace || "College Campus",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": event.eventPlace || "College Campus",
        "addressLocality": "Ajmer",
        "addressRegion": "Rajasthan",
        "postalCode": "305001",
        "addressCountry": "IN"
      }
    },
    "organizer": { // Fixes "Missing field organizer"
      "@type": "Organization",
      "name": "Rajput Shiksha Kosh",
      "url": window.location.origin
    },
    "image": [eventImage],
    "description": rawDescription
  };

  return (
    <>
      <Helmet>
        <title>{event.title} | Rajput Shiksha Kosh</title>
        <meta name="description" content={rawDescription} />
        <script type="application/ld+json">
          {JSON.stringify(eventSchemaMarkup)}
        </script>
      </Helmet>

      <div className="event-detail-page-wrapper">
        <div className="event-detail-page-container">
          
          <div className="event-detail-back-wrapper">
            <Link to="/events" className="event-detail-styled-btn">
              <FaArrowLeft className="event-detail-btn-icon" /> Back to Events
            </Link>
          </div>

          <div className="event-detail-page-header">
            <h1>{event.title}</h1>
            
            <div className="event-detail-page-divider">
              <span></span>
              <GiFairyWings />
              <span></span>
            </div>
            
            <div className="event-detail-page-meta-row">
                
                <div className="event-detail-styled-btn meta-tag">
                    <FaCalendarAlt className="event-detail-btn-icon" />
                    <span>{formatEventDateTime(event.eventDate, event.endDate, event.startTime, event.endTime)}</span>
                </div>

                <div className="event-detail-styled-btn meta-tag">
                    <FaMapMarkerAlt className="event-detail-btn-icon" />
                    <span>{event.eventPlace || "College Campus"}</span>
                </div>

            </div>
          </div>

          <div className="event-detail-page-body">
            {blocks.map((block, index) => {
              
              if (block.type === "text") {
                return (
                  <div
                    key={index}
                    className="event-detail-page-text"
                    dangerouslySetInnerHTML={{ __html: block.content }}
                  />
                );
              }

              if (block.type === "image") {
                const images = block.imageUrls || [];
                return (
                  <div key={index} className={`event-detail-page-gallery count-${images.length}`}>
                    {images.map((img, i) => (
                      <div key={i} className="event-detail-page-gallery-item">
                        <img src={`${API_BASE_URL}${img}`} alt="Event Moment" />
                      </div>
                    ))}
                  </div>
                );
              }

              return null;
            })}
          </div>

        </div>
      </div>
    </>
  );
}

export default EventDetail;